import React, { MutableRefObject } from "react";
import {Props} from "./AppBarMenu";

interface MenuItem {
    children?: MenuItem[];
}

type SetState<T> = React.Dispatch<React.SetStateAction<T>>;

const isForwardTab = (event: React.KeyboardEvent) => event.key === "Tab" && !event.shiftKey;
const isBackwardTab = (event: React.KeyboardEvent) => event.key === "Tab" && event.shiftKey;

export const handleMenuItemKeyboardNav = (
    event: React.KeyboardEvent,
    index: number,
    navigationRoot: { children: MenuItem[] },
    setIsKeyboardNavigation: SetState<boolean>,
    arrowButtonRefs: MutableRefObject<(HTMLButtonElement | null)[]>,
    lastFocusedMenuItemIndex: MutableRefObject<number | null>
): void => {
    setIsKeyboardNavigation(true);
    const nextMenuItem = navigationRoot.children[index + 1];
    const prevMenuItem = navigationRoot.children[index];
    if (isForwardTab(event)) {
        if (nextMenuItem && arrowButtonRefs.current[index]) {
            event.preventDefault();
            arrowButtonRefs.current[index]?.focus();
            lastFocusedMenuItemIndex.current = index
        }
    }
    else if (isBackwardTab(event)) {
        if (index !== 0 && prevMenuItem && arrowButtonRefs.current[index - 1]) {
            event.preventDefault();
            arrowButtonRefs.current[index - 1]?.focus();
            lastFocusedMenuItemIndex.current = index - 1;

        }
    }
};

export const handleMenuItemBlur = (
    event: React.FocusEvent,
    index: number,
    arrowButtonRefs: MutableRefObject<(HTMLButtonElement | null)[]>,
    setFocusedMenuItemIndex: SetState<number | null>
): void => {
    if (event.relatedTarget !== arrowButtonRefs.current[index]) {
        setFocusedMenuItemIndex(null);

    }
};

export const handleArrowKeyDown = (
    event: React.KeyboardEvent,
    index: number,
    menuItem: MenuItem,
    menuItemRefs: MutableRefObject<(HTMLAnchorElement | null)[]>,
    isMenuOpen: boolean,
    firstPopoverItemRef: React.MutableRefObject<HTMLButtonElement | HTMLDivElement | null>,
    setIsMenuOpen: SetState<boolean>,
    setActiveMenuItem: SetState<MenuItem | null>,
    setFocusedMenuItemIndex: SetState<number | null>,
    activeMenuItem: MenuItem | null
): void => {
    if (isForwardTab(event)) {
        const nextMenuItem = menuItemRefs.current[index + 1];
        const firstPopoverChild =
            firstPopoverItemRef.current?.querySelector<HTMLDivElement>(':scope > *'); // Select the first child of the ref

        const isLastMenuItem = !nextMenuItem;

        if (isMenuOpen && firstPopoverItemRef.current) {
            // Untermenü ist offen ➔ immer Fokus ins Untermenü setzen
            event.preventDefault();
            firstPopoverChild?.focus();
        } else if (!isLastMenuItem) {
            // Nicht letzter Menüpunkt ➔ Fokus auf nächsten Menüpunkt setzen
            event.preventDefault();
            nextMenuItem?.focus();
        } else {
            // Letzter Menüpunkt ohne geöffnetes Untermenü ➔ Fokus verlässt Navigation
            setFocusedMenuItemIndex(null);
        }
    } else if (event.key === "Enter") {
        event.preventDefault();
        if (activeMenuItem === menuItem) {
            setIsMenuOpen(false);
            setActiveMenuItem(null);
        } else {
            setActiveMenuItem(menuItem);
            setIsMenuOpen(true);
            setFocusedMenuItemIndex(index);
        }
    }
};


export const toggleSubmenu = (
    menuItem: MenuItem,
    index: number,
    activeMenuItem: MenuItem | null,
    setActiveMenuItem: SetState<MenuItem | null>,
    setIsMenuOpen: SetState<boolean>,
    setFocusedMenuItemIndex: SetState<number | null>,
    setIsKeyboardNavigation: SetState<boolean>,
): void => {
    setIsKeyboardNavigation(false);
    if (activeMenuItem === menuItem) {
        setIsMenuOpen(false);
        setActiveMenuItem(null);
    } else {
        setActiveMenuItem(menuItem);
        setIsMenuOpen(true);
        setFocusedMenuItemIndex(index);
    }

};

const handleTabOutOfThirdLevelItems = (
    lastActiveSecondLevelIndexRef: MutableRefObject<number | undefined>,
    setThirdLevelMenuItems: React.Dispatch<React.SetStateAction<any[]>>,
    props: Props,
    isReverse: boolean
) => {
    setThirdLevelMenuItems([]);

    const currentIndex = lastActiveSecondLevelIndexRef.current || 0;
    const newIndex = isReverse ? currentIndex : (currentIndex + 1) % props.selection?.children?.length;
    const targetSecondLevelItem = document.querySelector(
        `[data-second-level-index="${newIndex}"]`
    );

    if (targetSecondLevelItem) {
        const firstChild = targetSecondLevelItem.querySelector(":scope > *");
        if (firstChild instanceof HTMLElement) {
            firstChild.focus();
        }
        lastActiveSecondLevelIndexRef.current = newIndex;
    }
};

export const handleSecondLevelItemKeyDown = (
    event: React.KeyboardEvent,
    index: number,
    menuItem: any,
    props: Props,
    lastActiveSecondLevelIndexRef: MutableRefObject<number | undefined>
) => {
    const { nextMainNavItemRef, prevMainNavItemRef, closeMenu, selection } = props;
    const isFirstItem = index === 0;
    const isLastItem = index === selection?.children.length - 1;
    const isButton = event.target instanceof HTMLButtonElement;

    if (event.key === "Tab") {
        lastActiveSecondLevelIndexRef.current = isBackwardTab(event) ? index - 1 : index;
    }

    if (isForwardTab(event) && isLastItem && (isButton || !menuItem.children?.length)) {
        event.preventDefault();
        closeMenu();
        requestAnimationFrame(() => {
            nextMainNavItemRef?.focus();
        });
    }

    if (isBackwardTab(event) && isFirstItem && !isButton) {
        event.preventDefault();
        closeMenu();
        requestAnimationFrame(() => {
            prevMainNavItemRef?.focus();
        });
    }
};

export const handleThirdLevelItemKeyDown = (
    event: React.KeyboardEvent,
    isLastItem: boolean,
    isFirstItem: boolean,
    lastActiveSecondLevelIndexRef: MutableRefObject<number | undefined>,
    setThirdLevelMenuItems: React.Dispatch<React.SetStateAction<any[]>>,
    props: Props
) => {
    if (isLastItem && isForwardTab(event)) {
        event.preventDefault();
        handleTabOutOfThirdLevelItems(lastActiveSecondLevelIndexRef, setThirdLevelMenuItems, props, false);
    }
    if (isFirstItem && isBackwardTab(event)) {
        event.preventDefault();
        handleTabOutOfThirdLevelItems(lastActiveSecondLevelIndexRef, setThirdLevelMenuItems, props, true);
    }
};

